import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { QRCodeCanvas } from "qrcode.react";
import {
  Download, Share2, Copy, Check, Smartphone, Apple, Users, GraduationCap,
  Coins, HeartPulse, Sparkles, ScanLine, ShieldCheck, WifiOff, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";

const BENEFITS = [
  { icon: Users, title: "Kind Social", desc: "Connect with family, relatives, friends & community — a kinder, ad-free feed with posts, photos, comments and replies." },
  { icon: HeartPulse, title: "Be Well", desc: "Your Digital Twin: live vitals, a wellness index, steps leaderboard and Guardian SOS that shares your location in emergencies." },
  { icon: Coins, title: "Earn Together", desc: "Invite loved ones and earn across 3 tiers (9% · 6% · 3%). Credits, ventures and a fair, family-first economy." },
  { icon: Sparkles, title: "ANXYA — AI for everything", desc: "Your personal assistant. Draft posts, interpret documents, summarise books, plan your life and build for any need." },
  { icon: GraduationCap, title: "Learn & Grow", desc: "Courses, teachers and books — learn new skills and nurture mind, body and spirit, all in one place." },
  { icon: ScanLine, title: "Scan & Know", desc: "PurityPower scans products for safety and interprets books instantly — with a history you can revisit anytime." },
  { icon: ShieldCheck, title: "Private & Data-Sovereign", desc: "Your data stays yours. No ad tracking, strong privacy controls and public/private circles you fully control." },
  { icon: WifiOff, title: "Installs like a native app", desc: "Add to your home screen, launch full-screen and keep working even on a weak connection. No app store needed." },
];

export default function GetApp() {
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [canInstall, setCanInstall] = useState(!!(typeof window !== "undefined" && window.__mykindInstallPrompt));
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    setShareUrl(`${window.location.origin}/download`);
    const ua = window.navigator.userAgent || "";
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    setIsIOS(/iphone|ipad|ipod/i.test(ua) && !standalone);
    const onPrompt = (e) => { e.preventDefault(); window.__mykindInstallPrompt = e; setCanInstall(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const install = async () => {
    const evt = window.__mykindInstallPrompt;
    if (evt) {
      try { evt.prompt(); await evt.userChoice; window.__mykindInstallPrompt = null; setCanInstall(false); return; } catch (_) { /* fall through */ }
    }
    if (isIOS) {
      toast("Add My Kind to your iPhone", { description: "Tap the Share icon in Safari, then choose “Add to Home Screen”." });
    } else {
      toast("Install My Kind", { description: "Open your browser menu (⋮) and tap “Install app” / “Add to Home screen”." });
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); toast.success("Link copied — share it with anyone"); }
    catch { toast.error("Couldn't copy — long-press the link to copy"); }
  };

  const share = async () => {
    const data = { title: "My Kind — one app for everyone", text: "Join me on My Kind — connect, learn, earn and be well, as one family. Download here:", url: shareUrl };
    if (navigator.share) { try { await navigator.share(data); return; } catch (_) { /* cancelled */ } }
    copy();
  };

  const waText = encodeURIComponent(`Join me on My Kind 💛 — connect, learn, earn and be well, as one family.\nDownload the app: ${shareUrl}`);

  return (
    <div data-testid="get-app-page" className="max-w-6xl mx-auto px-5 sm:px-8 pt-[88px] pb-24">
      <Seo
        title="Download the My Kind app — connect, learn, earn & be well"
        description="Get My Kind on your phone in seconds. A kinder social home, live wellness, learning, earning and your own AI assistant — installs like a native app, no store needed."
        path="/download"
        image="/og/default.jpg"
      />

      {/* Hero */}
      <div className="grid lg:grid-cols-[1.3fr_1fr] gap-10 items-center">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="min-w-0">
          <div className="flex items-center gap-3">
            <img src="/mykind-appicon-512.png" alt="My Kind" className="w-14 h-14 rounded-2xl shadow-lg" />
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-brand">Download the app</div>
              <h1 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight">My Kind</h1>
            </div>
          </div>
          <p className="text-sec text-base sm:text-lg mt-5 leading-relaxed max-w-xl">
            One app for everything, for everyone. Connect with the people you love, stay well,
            learn, earn and get an AI assistant for everything — all in a kinder, private, ad-free home.
          </p>

          <div className="flex flex-wrap items-center gap-3 mt-7">
            <button data-testid="get-app-install-btn" onClick={install}
              className="btn-primary rounded-full h-12 px-6 text-sm font-medium flex items-center gap-2">
              <Download size={18} /> {isIOS ? "Add to iPhone" : "Install the app"}
            </button>
            <button data-testid="get-app-share-btn" onClick={share}
              className="rounded-full h-12 px-5 text-sm flex items-center gap-2 border border-brand/40 text-brand hover:bg-brand/10 transition-colors">
              <Share2 size={16} /> Share
            </button>
            <a data-testid="get-app-whatsapp" href={`https://wa.me/?text=${waText}`} target="_blank" rel="noopener noreferrer"
              className="rounded-full h-12 px-5 text-sm flex items-center gap-2 border border-rud text-sec hover:text-[var(--rud-text)] transition-colors">
              <Share2 size={16} /> WhatsApp
            </a>
          </div>

          {/* Copyable share link */}
          <div className="mt-5 flex items-center gap-2 max-w-md min-w-0">
            <div className="rud-input rounded-full px-4 h-11 flex items-center flex-1 min-w-0 text-sm text-sec truncate" data-testid="get-app-link">{shareUrl}</div>
            <button data-testid="get-app-copy-btn" onClick={copy}
              className="rounded-full h-11 w-11 grid place-items-center border border-rud text-sec hover:text-brand hover:border-brand/50 transition-colors shrink-0">
              {copied ? <Check size={17} className="text-brand" /> : <Copy size={17} />}
            </button>
          </div>
          <div className="flex items-center gap-4 mt-4 text-[12px] text-sec">
            <span className="flex items-center gap-1.5"><Smartphone size={14} className="text-brand" /> Android & Chrome</span>
            <span className="flex items-center gap-1.5"><Apple size={14} className="text-brand" /> iPhone & iPad</span>
          </div>
        </motion.div>

        {/* QR card */}
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.1 }}
          className="rud-card rounded-3xl p-7 text-center" data-testid="get-app-qr-card">
          <div className="bg-white rounded-2xl p-4 inline-block">
            {shareUrl ? <QRCodeCanvas value={shareUrl} size={176} level="M" includeMargin={false} data-testid="get-app-qr" /> : <div className="w-[176px] h-[176px]" />}
          </div>
          <p className="text-sm font-medium mt-4">Scan to download</p>
          <p className="text-sec text-[12px] mt-1 leading-relaxed">Point your phone camera at the code,<br />then tap “Install” or “Add to Home Screen”.</p>
        </motion.div>
      </div>

      {/* Benefits */}
      <div className="mt-16">
        <h2 className="font-serif text-2xl sm:text-3xl">Why you'll love it</h2>
        <p className="text-sec text-sm mt-1">Everything your family needs — in one caring app.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {BENEFITS.map((b, i) => (
            <motion.div key={b.title} data-testid={`benefit-${i}`}
              initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 4) * 0.06 }}
              className="rud-card rounded-2xl p-5 hover:border-brand/40 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-brand/15 grid place-items-center text-brand"><b.icon size={20} /></div>
              <h3 className="font-serif text-lg mt-4">{b.title}</h3>
              <p className="text-sec text-[13px] mt-1.5 leading-relaxed">{b.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* How to install */}
      <div className="mt-16 grid md:grid-cols-2 gap-5">
        <div className="rud-card rounded-2xl p-6" data-testid="install-steps-android">
          <div className="flex items-center gap-2 text-brand"><Smartphone size={18} /><span className="text-[11px] uppercase tracking-widest">Android · Chrome</span></div>
          <ol className="mt-4 space-y-2.5 text-sm text-sec list-decimal pl-5">
            <li>Tap <b className="text-[var(--rud-text)]">Install the app</b> above, or open the browser menu <b className="text-[var(--rud-text)]">⋮</b>.</li>
            <li>Choose <b className="text-[var(--rud-text)]">Install app</b> / <b className="text-[var(--rud-text)]">Add to Home screen</b>.</li>
            <li>My Kind appears on your home screen — open it full-screen, like a native app.</li>
          </ol>
        </div>
        <div className="rud-card rounded-2xl p-6" data-testid="install-steps-ios">
          <div className="flex items-center gap-2 text-brand"><Apple size={18} /><span className="text-[11px] uppercase tracking-widest">iPhone · Safari</span></div>
          <ol className="mt-4 space-y-2.5 text-sm text-sec list-decimal pl-5">
            <li>Open this page in <b className="text-[var(--rud-text)]">Safari</b>.</li>
            <li>Tap the <b className="text-[var(--rud-text)]">Share</b> icon, then <b className="text-[var(--rud-text)]">Add to Home Screen</b>.</li>
            <li>Tap <b className="text-[var(--rud-text)]">Add</b> — My Kind is now on your home screen.</li>
          </ol>
        </div>
      </div>

      {/* Footer CTA */}
      <div className="mt-16 rud-card rounded-3xl p-8 text-center bg-brand/5 border-brand/30" data-testid="get-app-cta">
        <Users className="mx-auto text-brand" size={28} />
        <h2 className="font-serif text-2xl sm:text-3xl mt-3">Bring your family in</h2>
        <p className="text-sec text-sm mt-2 max-w-lg mx-auto">Share this link with the people you love — the more who join, the kinder (and more rewarding) it gets.</p>
        <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
          <button onClick={install} className="btn-primary rounded-full h-12 px-6 text-sm font-medium flex items-center gap-2"><Download size={18} /> Get the app</button>
          <button onClick={share} className="rounded-full h-12 px-5 text-sm flex items-center gap-2 border border-brand/40 text-brand hover:bg-brand/10 transition-colors">Share with family <ArrowRight size={16} /></button>
        </div>
      </div>
    </div>
  );
}
